import React, { useState, useEffect } from 'react';
import { ModalWrapper } from './ModalWrapper';
import { POPUP_STYLES } from '../../constants/popupStyles';

export interface ColumnConfig {
    id: string;
    label: string;
    visible: boolean;
}

interface ManageColumnsModalProps {
    isOpen: boolean;
    onClose: () => void;
    columns: ColumnConfig[];
    onSave: (columns: ColumnConfig[]) => void;
    onReset?: () => void;
}

export const ManageColumnsModal: React.FC<ManageColumnsModalProps> = ({
    isOpen,
    onClose,
    columns,
    onSave,
    onReset,
}) => {
    const [localColumns, setLocalColumns] = useState<ColumnConfig[]>(columns);
    const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
    const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

    // Update local state when columns prop changes
    useEffect(() => {
        setLocalColumns(columns);
    }, [columns]);

    const handleDragStart = (index: number) => {
        setDraggedIndex(index);
    };

    const handleDragOver = (e: React.DragEvent, index: number) => {
        e.preventDefault();
        setDragOverIndex(index);
    };

    const handleDrop = (index: number) => {
        if (draggedIndex === null) return;

        const newColumns = [...localColumns];
        const draggedItem = newColumns[draggedIndex];

        // Remove from old position
        newColumns.splice(draggedIndex, 1);
        // Insert at new position
        newColumns.splice(index, 0, draggedItem);

        setLocalColumns(newColumns);
        setDraggedIndex(null);
        setDragOverIndex(null);
    };

    const handleDragEnd = () => {
        setDraggedIndex(null);
        setDragOverIndex(null);
    };

    const handleToggleVisibility = (id: string) => {
        setLocalColumns(prev =>
            prev.map(col =>
                col.id === id ? { ...col, visible: !col.visible } : col
            )
        );
    };

    const handleSave = () => {
        onSave(localColumns);
        onClose();
    };

    const handleReset = () => {
        if (onReset) {
            onReset();
            onClose();
        }
    };

    const handleCancel = () => {
        // Reset to original state
        setLocalColumns(columns);
        onClose();
    };

    const visibleCount = localColumns.filter(col => col.visible).length;

    return (
        <ModalWrapper modalId="ManageColumnsModal"
            isOpen={isOpen}
            onClose={handleCancel}
            title="Manage Columns"
            width="!max-w-2xl"
        >
            {/* Instructions */}
            <p className="text-sm text-[#366A75]/80 mb-4">
                Drag and drop to reorder columns. Use checkboxes to show or hide columns.
            </p>

            {/* Columns List */}
            <div className="max-h-96 overflow-y-auto border border-[#B4D9D4] rounded-2xl mb-5 bg-[#EBF5F5]">
                {localColumns.map((column, index) => {
                    const isDragging = draggedIndex === index;
                    const isDragOver = dragOverIndex === index;

                    return (
                        <div
                            key={column.id}
                            draggable
                            onDragStart={() => handleDragStart(index)}
                            onDragOver={(e) => handleDragOver(e, index)}
                            onDrop={() => handleDrop(index)}
                            onDragEnd={handleDragEnd}
                            className={`flex items-center px-4 py-3 cursor-move transition-colors ${index < localColumns.length - 1 ? 'border-b border-[#B4D9D4]' : ''
                                } ${isDragging ? 'opacity-50 bg-[#D0E7E5]' : isDragOver ? 'bg-[#D0E7E5]' : 'bg-transparent hover:bg-[#D0E7E5]/50'
                                }`}
                        >
                            {/* Drag Handle Icon */}
                            <div className="mr-3 text-[#366A75]/60 text-lg flex items-center">
                                ⋮⋮
                            </div>

                            {/* Checkbox */}
                            <input
                                type="checkbox"
                                checked={column.visible}
                                onChange={() => handleToggleVisibility(column.id)}
                                disabled={column.visible && visibleCount === 1}
                                className="mr-3 w-4 h-4 cursor-pointer disabled:cursor-not-allowed accent-[#366A75]"
                                onClick={(e) => e.stopPropagation()}
                            />

                            {/* Column Label */}
                            <span
                                className={`flex-1 text-sm ${column.visible ? 'text-[#366A75] font-medium' : 'text-[#366A75]/50 font-normal'
                                    }`}
                            >
                                {column.label}
                            </span>

                            {/* Visible indicator */}
                            {column.visible && (
                                <span className="text-xs text-green-600 font-medium">
                                    ✓ Visible
                                </span>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* Footer Info */}
            <div className="text-xs text-[#366A75]/70 mb-4">
                {visibleCount} of {localColumns.length} columns visible
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
                <button
                    onClick={handleSave}
                    className={POPUP_STYLES.button}
                >
                    Save
                </button>
                {onReset && (
                    <button
                        onClick={handleReset}
                        className={POPUP_STYLES.button}
                    >
                        Reset to Defaults
                    </button>
                )}
                <button
                    onClick={handleCancel}
                    className={POPUP_STYLES.button}
                >
                    Cancel
                </button>
            </div>
        </ModalWrapper>
    );
};
